import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { getCatalog, getProducts, resolveProductPrices } from '@/api/product.api';
import { slotPriceKey, type SlotPricePair } from '@/lib/slotPricing';
import { getProductAvailability } from '@/api/ticketSchedule.api';
import { getBundles, resolveBundlePrices, type ResolvedBundlePrice } from '@/api/bundle.api';
import { getPaymentMethods } from '@/api/paymentMethod.api';
import {
  getShiftSummary,
  lookupSales,
  getCurrentShift,
  openShift,
  closeShift,
  listMyApprovalRequests,
  getRefundableLines,
} from '@/api/sale.api';
import { getUserDevice } from '@/api/device.api';
import { getBlockedDates, findBlockForDate, type BlockedDate } from '@/api/blockedDates.api';
import { getBookingWindow } from '@/api/bookingWindow.api';
import { getGroupBookingTypes } from '@/api/groupBookingType.api';
import type { SearchParams } from '@/types/api';
import type { ResolvedPrices } from '@/types/product';

/** Every group type, in display order (#1726) — the promo "Segmen grup" picker. */
export function useGroupBookingTypes() {
  return useQuery({
    queryKey: ['group-booking-types'],
    queryFn: getGroupBookingTypes,
    staleTime: 5 * 60_000,
  });
}

export function useProducts(params?: SearchParams) {
  return useQuery({
    queryKey: ['products', params ?? null],
    queryFn: () => getProducts(params),
    staleTime: 60_000,
  });
}

/**
 * The counter's catalog for one visit date (`GET /product/catalog`): the
 * products on sale that day, each with the promos a live campaign gives it.
 * A slot ticket with nothing bookable that day is not in it. Keyed by date,
 * so switching dates never shows one day's promos against another's.
 */
export function useCatalog(
  visitDate: string,
  opts: { ticketCategory?: 'main' | 'attraction'; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['catalog', visitDate, opts.ticketCategory ?? null],
    queryFn: () => getCatalog(visitDate, { ticketCategory: opts.ticketCategory }),
    enabled: Boolean(visitDate) && (opts.enabled ?? true),
    staleTime: 30_000, // promos and bookable slots both move during the day
  });
}

export function useAvailability(productId: string | null, date: string, enabled = true) {
  return useQuery({
    queryKey: ['availability', productId, date],
    queryFn: () => getProductAvailability(productId as string, date),
    enabled: Boolean(productId) && enabled,
    staleTime: 15_000, // slot capacity moves
  });
}

export function useBundles(params?: SearchParams) {
  return useQuery({
    queryKey: ['bundles', params ?? null],
    queryFn: () => getBundles(params),
    staleTime: 60_000,
  });
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
    staleTime: 5 * 60_000,
  });
}

export function useShiftSummary(dateISO: string) {
  return useQuery({
    queryKey: ['shiftSummary', dateISO],
    queryFn: () => getShiftSummary(dateISO),
    staleTime: 30_000,
  });
}

/** The cashier's currently-open till session (null when none is open). */
export function useCurrentShift() {
  return useQuery({
    queryKey: ['shift', 'current'],
    queryFn: getCurrentShift,
    staleTime: 15_000,
  });
}

export function useOpenShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (openingFloat: number) => openShift(openingFloat),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'current'] }),
  });
}

export function useCloseShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      countedCash: number;
      denominations?: Record<string, number> | null;
      note?: string | null;
    }) => closeShift(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift', 'current'] });
      qc.invalidateQueries({ queryKey: ['shiftSummary'] });
    },
  });
}

export function useOrderLookup(
  params: { q: string; date: string; page: number; perPage: number; scope?: 'own' | 'all' },
  enabled = true,
) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => lookupSales(params),
    enabled,
    placeholderData: keepPreviousData, // keep the current page visible while the next loads
  });
}

export function useDeviceBinding() {
  return useQuery({
    queryKey: ['deviceBinding'],
    queryFn: getUserDevice,
    staleTime: 5 * 60_000,
  });
}

/**
 * PricingSchedule-aware prices for the visit date, keyed by product id. The
 * catalog base price is a weekday default; the real charge depends on the date
 * (weekend / holiday / school-holiday rules), so we resolve it here to keep the
 * displayed price in sync with what the sale will charge. `ids` is sorted into
 * the query key so the cache is stable regardless of input order.
 */
export function useResolvedProductPrices(ids: string[], date: string) {
  return useResolvedCatalogPrices(ids, date, selectPrices);
}

type ResolvedCatalogEntries = ResolvedPrices['prices'];

function selectPrices(prices: ResolvedCatalogEntries): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, p] of Object.entries(prices)) {
    if (typeof p?.price === 'number') out[id] = p.price;
  }
  return out;
}

function useResolvedCatalogPrices<T>(
  ids: string[],
  date: string,
  select: (prices: ResolvedCatalogEntries) => T,
) {
  const key = [...ids].sort();
  return useQuery({
    queryKey: ['resolvedProductPrices', date, key],
    queryFn: async () => {
      const res = await resolveProductPrices(
        key.map((productId) => ({ productId })),
        date,
      );
      return res.prices ?? {};
    },
    select,
    enabled: key.length > 0 && Boolean(date),
    staleTime: 60_000,
  });
}

/**
 * Prices for the (product, slot) pairs the cart holds, keyed by
 * `slotPriceKey(productId, slotId)`. A time-of-day rate gives one product
 * different prices in different slots; `useResolvedProductPrices` prices
 * against the slot running now, this prices against the slot the cashier
 * picked. A pair the backend refuses (a slot gone stale) costs only its own
 * batch: those lines keep the date price.
 */
export function useResolvedSlotPrices(pairs: SlotPricePair[], date: string) {
  return useQuery({
    queryKey: ['resolvedSlotPrices', date, pairs.map((p) => slotPriceKey(p.productId, p.slotId))],
    queryFn: async () => {
      const out: Record<string, number> = {};
      const batches: SlotPricePair[][] = [];
      for (let i = 0; i < pairs.length; i += SLOT_PRICE_BATCH) {
        batches.push(pairs.slice(i, i + SLOT_PRICE_BATCH));
      }
      await Promise.all(
        batches.map(async (batch) => {
          try {
            const res = await resolveProductPrices(batch, date);
            for (const [key, p] of Object.entries(res.slotPrices ?? {})) {
              if (typeof p?.price === 'number') out[key] = p.price;
            }
          } catch {
            /* this batch keeps the date price */
          }
        }),
      );
      return out;
    },
    enabled: pairs.length > 0 && Boolean(date),
    staleTime: 60_000,
  });
}

/** The backend caps resolve-prices at 100 items per call. */
const SLOT_PRICE_BATCH = 100;

/** PricingSchedule-aware bundle prices for the visit date, keyed by bundle id. */
export function useResolvedBundlePrices(ids: string[], date: string) {
  const key = [...ids].sort();
  return useQuery<Record<string, ResolvedBundlePrice>>({
    queryKey: ['resolvedBundlePrices', date, key],
    queryFn: () => resolveBundlePrices(key, date),
    enabled: key.length > 0 && Boolean(date),
    staleTime: 60_000,
  });
}

/**
 * Operator-blocked visit dates (public feed). Refetched on window focus and
 * every minute so a block created mid-shift reaches the counter quickly. On a
 * failed refetch react-query keeps the last good list, so a blip never
 * "un-blocks" a date we already knew about; the sale endpoint stays the
 * authoritative guard (409 VISIT_DATE_BLOCKED).
 */
export function useBlockedDates() {
  return useQuery({
    queryKey: ['blockedDates'],
    queryFn: getBlockedDates,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The park-wide booking window (#1488). Refreshed like the blocked-dates feed,
 * so a config change reaches the counter within a minute; `null` when the
 * feed is unavailable (the picker then uses the default window).
 */
export function useBookingWindow() {
  return useQuery({
    queryKey: ['bookingWindow'],
    queryFn: getBookingWindow,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** The block covering `visitDate` (YYYY-MM-DD), or null when it is sellable. */
export function useVisitDateBlock(visitDate: string): BlockedDate | null {
  const { data } = useBlockedDates();
  return findBlockForDate(visitDate, data);
}

/**
 * The operator's own void / refund requests. `refetchInterval` polls so a
 * supervisor's decision in the BO reaches the counter without a reload.
 */
export function useMyApprovalRequests(
  params: { status?: string; page?: number; perPage?: number },
  opts: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: ['approvalRequests', params],
    queryFn: () => listMyApprovalRequests(params),
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval ?? false,
    placeholderData: keepPreviousData,
  });
}

/** Lines of a sale with their unused-ticket counts (partial refund picker). */
export function useRefundableLines(saleRef: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['refundableLines', saleRef],
    queryFn: () => getRefundableLines(saleRef as string),
    enabled: Boolean(saleRef) && enabled,
    staleTime: 0,
  });
}
